import { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { useCheckout } from '@/hooks/useCheckout';
import { useTheme } from '@/App';

// ─── Shared plan data ─────────────────────────────────────────────────────────

export const LEAD_FEATURES   = ['Everything in Junior', 'Estimated Taxes & Payouts', 'Advanced Analytics', 'Invoice Management'];
export const JUNIOR_FEATURES = ['Booking Calendar', 'Hold Tracking', 'Income Dashboard'];

// ─── Currency-specific pricing (must match Stripe price IDs) ─────────────────

const PLAN_PRICES: Record<string, { sym: string; lead: string; junior: string }> = {
  USD: { sym: '$',  lead: '9.50',  junior: '5.50' },
  GBP: { sym: '£',  lead: '9.50',  junior: '5.50' },
  EUR: { sym: '€',  lead: '9.50',  junior: '5.50' },
};

function getPricing(currency: string) {
  return PLAN_PRICES[currency] ?? PLAN_PRICES['USD'];
}

// Instant fallback: derive currency from browser timezone.
function currencyFromTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Europe/London' || tz.startsWith('Europe/Belfast') || tz === 'Europe/Guernsey' || tz === 'Europe/Isle_of_Man' || tz === 'Europe/Jersey') return 'GBP';
    if (tz.startsWith('Europe/')) return 'EUR';
    if (tz.startsWith('America/')) {
      const canadianPrefixes = ['America/Toronto','America/Vancouver','America/Winnipeg','America/Edmonton','America/Halifax','America/St_Johns','America/Regina','America/Whitehorse','America/Yellowknife','America/Creston','America/Dawson','America/Moncton','America/Thunder_Bay'];
      if (canadianPrefixes.some(p => tz.startsWith(p))) return 'CAD';
    }
  } catch {}
  return 'USD';
}

// Map ISO 3166-1 country code → billing currency.
function currencyFromCountry(country: string): string {
  if (['GB', 'GG', 'IM', 'JE'].includes(country)) return 'GBP';
  if (['AT','BE','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','NL','PT','SK','SI','ES'].includes(country)) return 'EUR';
  if (country === 'CA') return 'CAD';
  return 'USD';
}

// Synchronous best-guess (timezone). Callers should also call detectBillingCurrencyAsync
// and update state when it resolves for an accurate IP-based result.
export function detectBillingCurrency(): string {
  return currencyFromTimezone();
}

// Async IP-based detection via api.country.is (free, no auth, no CORS).
export async function detectBillingCurrencyAsync(): Promise<string> {
  try {
    const res = await fetch('https://api.country.is/');
    if (res.ok) {
      const { country } = await res.json();
      if (country) return currencyFromCountry(country);
    }
  } catch {}
  return currencyFromTimezone();
}

// ─── Shared plan-card UI ──────────────────────────────────────────────────────

export function PlanCards({
  selectedPlan,
  onSelect,
  currency = 'USD',
}: {
  selectedPlan: 'lead' | 'junior';
  onSelect: (p: 'lead' | 'junior') => void;
  currency?: string;
}) {
  const pricing = getPricing(currency);
  const { isDark } = useTheme();

  const unselectedCls = isDark
    ? 'border-white/10 bg-white/5 hover:border-white/20'
    : 'border-gray-100 bg-gray-50/50 hover:border-gray-200';
  const priceCls  = isDark ? 'text-white' : 'text-[#1A1F36]';
  const featureCls = isDark ? 'text-white/60' : 'text-[#1A1F36]/70';
  const checkUnselected = isDark ? 'text-white/20 shrink-0' : 'text-gray-300 shrink-0';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Lead card */}
      <button
        onClick={() => onSelect('lead')}
        className={`rounded-[2rem] p-6 text-left transition-all border-2 ${
          selectedPlan === 'lead' ? 'border-[#4A6CF7] bg-[#4A6CF7]/5' : unselectedCls
        }`}
      >
        <div className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] mb-3">LEAD PLAN</div>
        <div className="flex items-baseline gap-1 mb-4">
          <span className={`text-3xl font-black ${priceCls}`}>{pricing.sym}{pricing.lead}</span>
          <span className="text-xs font-bold text-gray-400">/mo</span>
        </div>
        <ul className="space-y-2">
          {LEAD_FEATURES.map(f => (
            <li key={f} className={`flex items-center gap-2 text-xs font-bold ${featureCls}`}>
              <Check size={12} className={selectedPlan === 'lead' ? 'text-[#4A6CF7] shrink-0' : checkUnselected} />
              {f}
            </li>
          ))}
        </ul>
      </button>

      {/* Junior card */}
      <button
        onClick={() => onSelect('junior')}
        className={`rounded-[2rem] p-6 text-left transition-all border-2 ${
          selectedPlan === 'junior' ? 'border-[#4A6CF7] bg-[#4A6CF7]/5' : unselectedCls
        }`}
      >
        <div className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7] mb-3">JUNIOR PLAN</div>
        <div className="flex items-baseline gap-1 mb-4">
          <span className={`text-3xl font-black ${priceCls}`}>{pricing.sym}{pricing.junior}</span>
          <span className="text-xs font-bold text-gray-400">/mo</span>
        </div>
        <ul className="space-y-2">
          {JUNIOR_FEATURES.map(f => (
            <li key={f} className={`flex items-center gap-2 text-xs font-bold ${featureCls}`}>
              <Check size={12} className={selectedPlan === 'junior' ? 'text-[#4A6CF7] shrink-0' : checkUnselected} />
              {f}
            </li>
          ))}
        </ul>
      </button>
    </div>
  );
}

// ─── Modal (dismissible — used from TrialBanner "Choose Plan") ────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SubscriptionModal({ open, onClose }: Props) {
  const [selectedPlan, setSelectedPlan] = useState<'lead' | 'junior'>('lead');
  const [billingCurrency, setBillingCurrency] = useState(detectBillingCurrency());
  const { isDark } = useTheme();
  const { startCheckout, isLoading, error } = useCheckout();
  const pricing = getPricing(billingCurrency);

  useEffect(() => {
    detectBillingCurrencyAsync().then(c => setBillingCurrency(c));
  }, []);

  if (!open) return null;

  const headingCls  = isDark ? 'text-white' : 'text-[#1A1F36]';
  const subTextCls  = isDark ? 'text-white/40' : 'text-[#1A1F36]/40';
  const cardBg      = isDark
    ? 'bg-white/10 border-white/10 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#131820]'
    : 'bg-white/90 border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white';
  const closeBtnCls = isDark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-[#1A1F36]/40 hover:text-[#1A1F36] hover:bg-black/5';
  const btnCls      = isDark ? 'bg-white text-[#1A1F36] hover:bg-white/90' : 'bg-black text-white hover:bg-gray-900';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-xl bg-black/40 overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-black/80"
      onClick={onClose}
    >
      <div
        className={`${cardBg} border backdrop-blur-2xl rounded-[2.5rem] p-4 sm:p-6 md:p-10 max-w-[95vw] lg:max-w-2xl w-full shadow-2xl relative`}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className={`absolute top-6 right-6 p-2 rounded-full transition-colors ${closeBtnCls}`}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 bg-black text-white rounded-full w-fit mb-6">
          <span className="text-[10px] font-black uppercase tracking-[0.3em]">CHOOSE YOUR PLAN</span>
        </div>

        <h2 className={`text-4xl font-black tracking-tight ${headingCls}`}>
          Don't lose visibility on your business.
        </h2>
        <p className={`text-[11px] font-bold uppercase tracking-[0.2em] mt-2 mb-8 ${subTextCls}`}>
          Subscribe to keep access to your earnings tracking, invoice history, tax estimates — and more.
        </p>

        <PlanCards selectedPlan={selectedPlan} onSelect={setSelectedPlan} currency={billingCurrency} />

        {error && (
          <p className="text-[10px] font-bold text-red-500 mt-4 text-center">{error}</p>
        )}

        <button
          onClick={() => startCheckout(selectedPlan)}
          disabled={isLoading}
          className={`w-full mt-6 rounded-full py-4 font-black uppercase tracking-[0.2em] text-[10px] transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${btnCls}`}
        >
          {isLoading
            ? 'Redirecting to Stripe...'
            : `Subscribe to ${selectedPlan === 'lead' ? 'Lead' : 'Junior'} — ${pricing.sym}${selectedPlan === 'lead' ? pricing.lead : pricing.junior}/month`}
        </button>

        <p className={`text-[10px] font-bold text-center uppercase tracking-[0.2em] mt-3 ${subTextCls}`}>
          Secured by Stripe
        </p>
      </div>
    </div>
  );
}
